import type { TratoPerfil } from '@actual-app/core/types/prefs';
/**
 * «¿Y cómo te hablo?» (maqueta v5 de la Primera travesía, 13-oct-2026): la
 * forma con la que Gavi habla a la persona. Se guarda en la pref global
 * `tratoPerfil` (del dispositivo, como `nombrePerfil`).
 *
 * Cuatro estados: sin elegir (lo de siempre: frases sin palabras con género),
 * femenino, masculino y neutro (las formas con -e: «liste», «farere»). Las
 * frases que cambian usan el `context` de i18next: la clave base es la de sin
 * elegir y `clave_female`, `clave_male` y `clave_neutral` son sus variantes
 * en `locale-overrides/es.json`. Si una variante falta, sale la base.
 *
 * Gavi habla siempre de sí misma en femenino («Encantada», «Yo nací lista»).
 */
import type { TFunction } from 'i18next';

import { useGlobalPref } from '#hooks/useGlobalPref';

export type Trato = TratoPerfil;

/** Contexto de i18next de cada forma. */
export type ContextoTrato = 'female' | 'male' | 'neutral';

/** Las opciones, en el orden de la maqueta. */
export const TRATOS: readonly Trato[] = ['femenino', 'masculino', 'neutro'];

/** Valor guardado → forma elegida (lo raro o vacío = sin elegir). */
export function normalizarTrato(valor: unknown): Trato | null {
  return TRATOS.find(x => x === valor) ?? null;
}

/** Forma → `context` de i18next; sin elegir, ninguno (la clave base). */
export function contextoTrato(trato: unknown): ContextoTrato | undefined {
  switch (normalizarTrato(trato)) {
    case 'femenino':
      return 'female';
    case 'masculino':
      return 'male';
    case 'neutro':
      return 'neutral';
    default:
      return undefined;
  }
}

/** Nombre de cada forma, como en el selector («En femenino»). */
export function nombreTrato(t: TFunction, trato: Trato): string {
  switch (trato) {
    case 'femenino':
      return t('In feminine');
    case 'masculino':
      return t('In masculine');
    default:
      return t('In neutral');
  }
}

/** Tocar la opción marcada la quita (vuelve a «sin elegir»). */
export function alternarTrato(actual: Trato | null, tocada: Trato) {
  return actual === tocada ? null : tocada;
}

/**
 * La forma elegida en este dispositivo, su `context` para `t()` y cómo
 * cambiarla (`null` = sin elegir).
 */
export function useTrato() {
  const [guardado, guardar] = useGlobalPref('tratoPerfil');
  const trato = normalizarTrato(guardado);
  return {
    trato,
    contexto: contextoTrato(trato),
    setTrato: (nuevo: Trato | null) => guardar(nuevo ?? ''),
  };
}
